/**
 * DroppableColumn - A column that can receive dropped blocks
 */

import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { EmailColumn, ContentBlock, Selection } from './types';
import { SortableBlock } from './SortableBlock';
import { Plus } from 'lucide-react';

interface DroppableColumnProps {
  column: EmailColumn;
  rowId: string;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  onDeleteBlock: (blockId: string) => void;
  onUpdateBlock: (blockId: string, updates: Partial<ContentBlock>) => void;
  onMoveBlock: (blockId: string, targetColumnId: string, targetIndex: number) => void;
  onBlockHover?: (blockId: string | null) => void;
  isFirstRow?: boolean;
  isLastColumn?: boolean;
}

export const DroppableColumn: React.FC<DroppableColumnProps> = ({
  column,
  rowId,
  selection,
  onSelect,
  onDeleteBlock,
  onUpdateBlock,
  onMoveBlock,
  onBlockHover,
  isFirstRow = false,
  isLastColumn = false,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { type: 'column', columnId: column.id },
  });

  const isSelected = selection.type === 'column' && selection.columnId === column.id;
  const hasContent = column.blocks.length > 0;

  // Only show border on hover/selection, or when empty (to indicate drop zone)
  const showBorder = isOver || isSelected || !hasContent;

  return (
    <div
      ref={setNodeRef}
      style={{ 
        width: `${column.width}%`,
        // Transparent background to inherit row color, only highlight on interaction
        backgroundColor: isOver ? 'rgba(219, 234, 254, 0.5)' : isSelected ? 'rgba(239, 246, 255, 0.3)' : 'transparent',
        borderColor: isOver ? '#3b82f6' : isSelected ? '#60a5fa' : 'transparent',
        borderWidth: showBorder ? '2px' : '0px',
        borderStyle: 'dashed',
        // Consistent min-height to prevent layout shifts
        minHeight: hasContent ? '40px' : '60px',
        overflow: 'visible', // Ensure content isn't clipped
      }}
      className="transition-all"
      onClick={(e) => {
        e.stopPropagation();
        onSelect({ type: 'column', rowId, columnId: column.id });
      }}
    >
      <SortableContext
        items={column.blocks.map(b => b.id)}
        strategy={verticalListSortingStrategy}
      >
        {column.blocks.length === 0 ? (
          /* Empty column state - subtle drop target indicator */
          <div 
            className="h-full min-h-[60px] flex items-center justify-center transition-colors"
            style={{ backgroundColor: isOver ? 'rgba(219, 234, 254, 0.5)' : 'transparent' }}
          >
            <div 
              className="text-center p-2"
              style={{ color: isOver ? '#2563eb' : '#9ca3af' }}
            >
              <Plus className="w-5 h-5 mx-auto mb-0.5" />
              <span className="text-xs">Drop here</span>
            </div>
          </div>
        ) : (
          /* Blocks - render directly without extra wrapper */
          column.blocks.map((block) => (
            <SortableBlock
              key={block.id}
              block={block}
              columnId={column.id}
              isSelected={selection.type === 'block' && selection.blockId === block.id}
              onSelect={() => onSelect({ type: 'block', rowId, columnId: column.id, blockId: block.id })}
              onDelete={() => onDeleteBlock(block.id)}
              onUpdate={(updates) => onUpdateBlock(block.id, updates)}
              onHover={onBlockHover}
              isFirstRow={isFirstRow}
              isLastColumn={isLastColumn}
            />
          ))
        )}
      </SortableContext>
    </div>
  );
};

